/** One row of the anatomy tree.
 *
 *  Three columns on a CSS grid, matching the timeline's density idiom: the
 *  source line, the disclosure gutter (indented by depth), then the content.
 *  A line number is the thing ops actually needs to jump to the editor, so it
 *  gets the left column rather than being buried in a tooltip.
 */
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material';

import type { AnatomyBadge, AnatomyVerdict } from '@/api/notificationAnatomy';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { blockActions, featureActions } from '../notifications/notificationLinks';
import { editorLink } from './editorLink';
import type { FlatRow } from './model/types';

/** Kinds that carry structural weight and read better in medium ink. */
const STRUCTURAL = new Set([
  'notification',
  'delivery',
  'layout',
  'standard-template',
  'layout-block',
  'subject',
  'blocks',
  'block',
  'advanced',
  'advanced-rule',
  'ast',
]);

const BADGE_COLOR: Record<string, 'default' | 'warning' | 'error' | 'info'> = {
  switch: 'warning',
  tour_id: 'warning',
  language: 'info',
  unknown_block: 'error',
  unclosed: 'error',
  block_ref: 'default',
  feature: 'info',
  disabled: 'default',
};

/** Kinds whose LABEL already opens with the keyword ("if …", "for …",
 *  "if-ruleset …", "elif …"). Printing the kind too produced the "if if" and
 *  "branch if" stutter that made a collapsed tree unreadable. */
const LABEL_CARRIES_KEYWORD: ReadonlySet<string> = new Set([
  'if',
  'for',
  'ifruleset',
  'branch',
]);

function KindTag({ kind }: { kind: string }) {
  if (STRUCTURAL.has(kind) || LABEL_CARRIES_KEYWORD.has(kind)) return null;
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.6875rem',
        color: 'text.disabled',
        flexShrink: 0,
      }}
    >
      {kind}
    </Box>
  );
}

function Badges({
  badges,
  onJumpToBlock,
}: {
  badges: AnatomyBadge[];
  onJumpToBlock?: (alias: string) => void;
}) {
  if (!badges.length) return null;
  return (
    <>
      {badges.map((badge, i) => {
        // A `block.x` reference is the seam between the layout and the block
        // that fills it, so it is the one badge worth making navigable.
        const jump =
          badge.code === 'block_ref' && badge.ref && onJumpToBlock
            ? () => onJumpToBlock(badge.ref!)
            : undefined;
        const chip = (
          <Chip
            key={`${badge.code}-${i}`}
            label={badge.label}
            size="small"
            color={BADGE_COLOR[badge.code] ?? 'default'}
            variant={BADGE_COLOR[badge.code] === 'default' ? 'outlined' : 'filled'}
            {...(jump
              ? {
                  clickable: true,
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    jump();
                  },
                }
              : {})}
            sx={{ height: 18, fontSize: '0.6875rem', flexShrink: 0 }}
          />
        );
        // The feature badge names a gate; the flag rows that implement it live
        // on the feature-flags grid.
        if (badge.code === 'feature' && badge.ref) {
          return (
            <ValueActions key={`${badge.code}-${i}`} chip actions={featureActions(badge.ref)}>
              {chip}
            </ValueActions>
          );
        }
        return chip;
      })}
    </>
  );
}


/** How a verdict reads on a row.
 *
 *  `dim` is the load-bearing one: a skipped or unreached node did not contribute
 *  to the output, and dimming is what makes the *taken* path legible at a
 *  glance. `in_loop` is deliberately NOT dimmed — it is a refusal to answer,
 *  and dimming it would read as "did not fire".
 */
const VERDICT_UI: Record<
  AnatomyVerdict['state'],
  { chip: string | null; color: 'success' | 'default' | 'warning' | 'error' | 'info'; dim: boolean }
> = {
  taken: { chip: null, color: 'success', dim: false },
  skipped: { chip: 'not taken', color: 'default', dim: true },
  not_reached: { chip: 'not reached', color: 'default', dim: true },
  in_loop: { chip: 'in loop — not evaluated', color: 'info', dim: false },
  error: { chip: 'evaluation error', color: 'error', dim: false },
  empty: { chip: 'rendered empty', color: 'warning', dim: false },
  value: { chip: null, color: 'success', dim: false },
};

function VerdictChip({ verdict }: { verdict: AnatomyVerdict | undefined }) {
  if (!verdict) return null;
  const ui = VERDICT_UI[verdict.state];
  if (!ui?.chip) return null;
  return (
    <Tooltip describeChild title={verdict.detail ?? ''} disableHoverListener={!verdict.detail}>
      <Chip
        label={ui.chip}
        size="small"
        color={ui.color}
        variant={ui.color === 'default' ? 'outlined' : 'filled'}
        sx={{ height: 18, fontSize: '0.6875rem', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

interface Props {
  row: FlatRow;
  selected: boolean;
  /** Needed to build editor links — a line only means something for one record. */
  notificationId: number;
  /** Tint rows whose content comes from a block rather than the layout. */
  highlightBlocks?: boolean;
  onToggle(id: string): void;
  onSelect(id: string): void;
  onJumpToBlock?(alias: string): void;
}

export function AnatomyNodeRow({
  row,
  selected,
  notificationId,
  highlightBlocks = false,
  onToggle,
  onSelect,
  onJumpToBlock,
}: Props) {
  const { node, depth, hasChildren, expanded } = row;
  const isTruncation = node.kind === 'truncation';
  const link = editorLink(notificationId, node.editTarget, node.line);
  const fromBlock = highlightBlocks && Boolean(node.withinBlock);
  // A missing verdict means "not measured" (a static anatomy), never "false" —
  // so nothing is dimmed until a trace has actually run.
  const dimmed = node.verdict ? (VERDICT_UI[node.verdict.state]?.dim ?? false) : false;

  return (
    <Box
      onClick={() => onSelect(node.id)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '3.5rem 1fr',
        alignItems: 'start',
        cursor: 'pointer',
        borderRadius: 0.5,
        // A tone step, not a stripe: the design system reserves side accents for
        // the nav rail's active bar.
        bgcolor: selected ? 'action.selected' : fromBlock ? 'action.hover' : undefined,
        opacity: dimmed ? 0.45 : 1,
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover', opacity: 1 },
      }}
    >
      <Box sx={{ textAlign: 'right', pr: 1, pt: 0.5 }}>
        {node.line ? (
          link && link.exact ? (
            // `describeChild` keeps the link's accessible name as "L536" with the
            // tooltip as its description. Without it MUI makes the tooltip the
            // NAME and the line number disappears for a screen reader
            // (components.md, "Deep-linking a record").
            <Tooltip describeChild title={link.label}>
              <Link
                href={link.href}
                target="_blank"
                rel="noopener"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.6875rem',
                  fontVariantNumeric: 'tabular-nums',
                  textDecorationStyle: 'dotted',
                }}
              >
                L{node.line}
              </Link>
            </Tooltip>
          ) : (
            // No exact target (a shared Standard_Template block, or a block
            // definition whose dialog has no line to reveal): show the number,
            // offer no link that would not land.
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.6875rem',
                color: 'text.disabled',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              L{node.line}
            </Typography>
          )
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          pl: `${depth * 0.875}rem`,
          py: 0.125,
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            aria-label={expanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            sx={{ p: 0, flexShrink: 0 }}
          >
            {expanded ? (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 16, flexShrink: 0 }} />
        )}

        <KindTag kind={node.kind} />

        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: STRUCTURAL.has(node.kind) ? 600 : 400,
            color: isTruncation
              ? 'warning.main'
              : node.kind === 'text'
                ? 'text.secondary'
                : 'text.primary',
            fontStyle: isTruncation ? 'italic' : undefined,
          }}
        >
          {node.layoutBlockId != null ? (
            <ValueActions actions={blockActions({ id: node.layoutBlockId, codename: node.label })}>
              {node.label}
            </ValueActions>
          ) : (
            node.label
          )}
        </Typography>

        {node.detail && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
            }}
          >
            {node.detail}
          </Typography>
        )}

        <VerdictChip verdict={node.verdict} />

        <Badges badges={node.badges} onJumpToBlock={onJumpToBlock} />

        {/* The collapse hint: how much is hidden behind a closed row. */}
        {hasChildren && !expanded && node.descendants > 0 && (
          <Tooltip describeChild title={`${node.descendants} nodes beneath`}>
            <Chip
              label={node.descendants}
              size="small"
              variant="outlined"
              sx={{
                height: 18,
                fontSize: '0.6875rem',
                flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
